import React, { useState, useEffect } from 'react';
import { 
    Box, 
    Paper, 
    Table, 
    TableBody, 
    TableCell, 
    TableContainer, 
    TableHead, 
    TableRow,
    Typography,
    TextField,
    MenuItem,
    Button,
    FormControl,
    InputLabel,
    Select,
    CircularProgress,
    Grid,
    Autocomplete,
    Chip,
    Tooltip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Slider,
    IconButton,
    TablePagination,
    Checkbox,
    TableSortLabel,
    Radio,
    RadioGroup,
    FormControlLabel,
    FormLabel
} from '@mui/material';
import axios from 'axios';
import api from '../../../admin/utils/api';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SettingsIcon from '@mui/icons-material/Settings';
import RefreshIcon from '@mui/icons-material/Refresh';
import InfoIcon from '@mui/icons-material/Info';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

// Получаем API URL из переменной окружения
const API_URL = process.env.REACT_APP_API_URL || '';

// Функции для определения цвета в зависимости от значения
const getRoiColor = (roi) => {
    if (roi > 100) return 'success.main'; // Темно-зеленый для очень хорошего ROI (>100%)
    if (roi > 0) return 'success.light'; // Светло-зеленый для положительного ROI
    if (roi < -20) return 'error.main'; // Темно-красный для очень плохого ROI (<-20%)
    if (roi < 0) return 'error.light'; // Светло-красный для отрицательного ROI
    return 'text.primary'; // Обычный цвет для нуля
};

const getCRColor = (cr) => {
    if (cr > 15) return 'success.main'; // Очень хорошая конверсия
    if (cr > 5) return 'success.light'; // Хорошая конверсия
    if (cr < 1) return 'error.light'; // Плохая конверсия
    return 'text.primary'; // Обычный цвет
};

const getProfitColor = (profit) => {
    if (profit > 5000) return 'success.main'; // Очень хорошая прибыль
    if (profit > 0) return 'success.light'; // Положительная прибыль
    if (profit < -1000) return 'error.main'; // Большой убыток
    if (profit < 0) return 'error.light'; // Убыток
    return 'text.primary'; // Нулевая прибыль
};

// Функция для определения цвета Аппрув-рейта
const getApproveRateColor = (rate) => {
    if (rate > 80) return 'success.main'; // Отличный аппрув-рейт
    if (rate > 50) return 'success.light'; // Хороший аппрув-рейт
    if (rate < 30) return 'error.main'; // Плохой аппрув-рейт
    if (rate < 50) return 'error.light'; // Низкий аппрув-рейт
    return 'text.primary'; // Средний аппрув-рейт
};

// Базовые пороговые значения (дефолтные)
const DEFAULT_PERFORMANCE_THRESHOLDS = {
    minRoi: 20, // Минимально приемлемый ROI в процентах
    minCr: 3,   // Минимально приемлемая конверсия в процентах
    minClicks: 500 // Минимальное количество кликов для достоверной статистики
};

// Модифицируем функцию оценки эффективности, чтобы принимать пользовательские пороги
const evaluatePlatformPerformance = (platformData, globalAverages, customThresholds = DEFAULT_PERFORMANCE_THRESHOLDS, isFiltered = false) => {
    const warnings = [];
    let score = 100; // Начальная оценка
    
    // Используем предоставленные пороги или дефолтные
    const thresholds = {
        ...DEFAULT_PERFORMANCE_THRESHOLDS,
        ...customThresholds
    };
    
    // Расчет метрик
    const cr = platformData.clicks > 0 ? (platformData.leads / platformData.clicks * 100) : 0;
    const profit = platformData.revenue - platformData.cost;
    const roi = platformData.cost > 0 ? ((profit / platformData.cost) * 100) : 0;
    
    // Проверка статистической значимости - если применен фильтр по объявлению,
    // используем меньшие пороговые значения, так как данных будет меньше
    const minClicksThreshold = isFiltered ? Math.floor(thresholds.minClicks / 3) : thresholds.minClicks;
    
    // Проверяем только количество кликов, без учета расходов
    const hasEnoughData = platformData.clicks >= minClicksThreshold;
    
    if (!hasEnoughData) {
        warnings.push({
            type: 'data',
            severity: 'info',
            message: `Недостаточно данных: ${platformData.clicks} кликов при минимуме ${minClicksThreshold}`
        });
        
        // Возвращаем нейтральный статус для площадок с недостаточными данными
        return {
            score: 50, // Нейтральная оценка
            status: 'info', // Новый статус - информационный
            warnings,
            hasEnoughData: false
        };
    }
    
    // Только если есть достаточно данных, проводим остальные проверки
    
    // Проверка ROI
    if (roi < thresholds.minRoi) {
        warnings.push({
            type: 'roi',
            severity: roi < 0 ? 'high' : 'medium',
            message: `ROI ниже целевого: ${roi.toFixed(2)}%`
        });
        score -= (thresholds.minRoi - roi) * 2;
    }
    
    // Проверка CR
    if (cr < thresholds.minCr) {
        warnings.push({
            type: 'cr',
            severity: cr < globalAverages.cr / 2 ? 'high' : 'medium',
            message: `Низкая конверсия: ${cr.toFixed(2)}%`
        });
        score -= (thresholds.minCr - cr) * 10;
    }
    
    return {
        score: Math.max(0, Math.min(100, score)), // Ограничиваем от 0 до 100
        status: score < 40 ? 'critical' : score < 70 ? 'warning' : 'good',
        warnings,
        hasEnoughData: true
    };
};

// Компонент для отображения статуса площадки
const PerformanceStatus = ({ status, warnings }) => {
    let icon, color;
    
    switch (status) {
        case 'critical':
            icon = <ErrorIcon />;
            color = 'error';
            break;
        case 'warning':
            icon = <WarningIcon />;
            color = 'warning';
            break;
        case 'info':
            icon = <Tooltip title="Недостаточно данных для анализа">
                    <Chip 
                        label="Мало данных" 
                        color="info" 
                        size="small" 
                        variant="outlined" 
                    />
                  </Tooltip>;
            return icon;
        default:
            icon = <CheckCircleIcon />;
            color = 'success';
    }
    
    return (
        <Tooltip title={
            warnings.length > 0 
                ? warnings.map(warning => `${warning.message}`).join('\n') 
                : "Показатели в норме"
        }>
            <Chip 
                icon={icon} 
                label={status === 'critical' ? 'Проблема' : status === 'warning' ? 'Внимание' : 'Норма'} 
                color={color} 
                size="small" 
                variant="outlined" 
            />
        </Tooltip>
    );
};

// Компонент для отображения списка проблемных площадок
const ProblemSitesList = ({ sites, globalAverages, thresholds = DEFAULT_PERFORMANCE_THRESHOLDS, isFiltered = false }) => {
    // Оценка эффективности для каждого сайта
    const sitesWithPerformance = sites.map(site => {
        const cr = site.clicks > 0 ? (site.leads / site.clicks * 100) : 0;
        const profit = site.revenue - site.cost;
        const roi = site.cost > 0 ? ((profit / site.cost) * 100) : 0;
        
        const performance = evaluatePlatformPerformance(
            { ...site, cr, roi, profit },
            globalAverages,
            thresholds,
            isFiltered
        );
        
        return { ...site, performance, cr, roi, profit };
    });
    
    // Фильтруем только проблемные площадки с достаточным количеством данных
    const problemSites = sitesWithPerformance
        .filter(site => site.performance.status === 'critical' && site.performance.hasEnoughData)
        .sort((a, b) => a.performance.score - b.performance.score); // Сначала с самыми низкими оценками
    
    if (problemSites.length === 0) {
        return (
            <Paper sx={{ p: 2, mb: 3, bgcolor: 'success.light', color: 'white' }}>
                <Typography variant="subtitle1">
                    {isFiltered 
                        ? 'Для выбранного объявления нет проблемных площадок'
                        : 'На данный момент нет площадок, требующих срочного внимания'}
                </Typography>
            </Paper>
        );
    }
    
    return (
        <Paper sx={{ p: 2, mb: 3, bgcolor: 'error.light', color: 'white' }}>
            <Typography variant="subtitle1" gutterBottom>
                {isFiltered 
                    ? `Для выбранного объявления обнаружены проблемные площадки (${problemSites.length})`
                    : `Обнаружены площадки, требующие срочного внимания (${problemSites.length})`}
            </Typography>
            <Box sx={{ maxHeight: '200px', overflow: 'auto' }}>
                {problemSites.map(site => (
                    <Box key={site.siteId} sx={{ p: 1, borderBottom: '1px solid rgba(255,255,255,0.2)' }}>
                        <Grid container alignItems="center" spacing={1}>
                            <Grid item xs={2}>
                                <strong>ID: {site.siteId}</strong>
                            </Grid>
                            <Grid item xs={4}>
                                {site.performance.warnings.map((warning, idx) => (
                                    <Typography key={idx} variant="body2">
                                        {warning.message}
                                    </Typography>
                                ))}
                            </Grid>
                            <Grid item xs={2}>
                                <Typography variant="body2">
                                    Клики: {site.clicks}
                                </Typography>
                                <Typography variant="body2">
                                    Лиды: {site.leads}
                                </Typography>
                            </Grid>
                            <Grid item xs={2}>
                                <Typography variant="body2">
                                    CR: {site.cr.toFixed(2)}%
                                </Typography>
                                <Typography variant="body2">
                                    ROI: {site.roi.toFixed(2)}%
                                </Typography>
                            </Grid>
                            <Grid item xs={2}>
                                <Typography variant="body2">
                                    Расход: {site.cost.toFixed(2)} руб.
                                </Typography>
                                <Typography variant="body2">
                                    Прибыль: {site.profit.toFixed(2)} руб.
                                </Typography>
                            </Grid>
                        </Grid>
                    </Box>
                ))}
            </Box>
        </Paper>
    );
};

// Диалог настройки пороговых значений
const ThresholdsSettingsDialog = ({ open, onClose, thresholds, onSave }) => {
    const [localThresholds, setLocalThresholds] = useState({ ...thresholds });
    
    useEffect(() => {
        setLocalThresholds({ ...thresholds });
    }, [thresholds, open]);
    
    const handleChange = (name, value) => {
        setLocalThresholds(prev => ({
            ...prev,
            [name]: value
        }));
    };
    
    const handleSave = () => {
        onSave(localThresholds);
        onClose();
    };
    
    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Настройки параметров эффективности</DialogTitle>
            <DialogContent>
                <Box sx={{ mt: 2 }}>
                    <Typography gutterBottom>
                        Минимальный приемлемый ROI (%): {localThresholds.minRoi}
                    </Typography>
                    <Slider
                        value={localThresholds.minRoi}
                        onChange={(_, value) => handleChange('minRoi', value)}
                        min={-50}
                        max={200}
                        step={5}
                        valueLabelDisplay="auto"
                        marks={[
                            { value: 0, label: '0%' },
                            { value: 100, label: '100%' }
                        ]}
                    />
                    
                    <Typography gutterBottom sx={{ mt: 3 }}>
                        Минимальная приемлемая конверсия (CR, %): {localThresholds.minCr}
                    </Typography>
                    <Slider
                        value={localThresholds.minCr}
                        onChange={(_, value) => handleChange('minCr', value)}
                        min={0}
                        max={10}
                        step={0.1}
                        valueLabelDisplay="auto"
                        marks={[
                            { value: 0, label: '0%' },
                            { value: 5, label: '5%' },
                            { value: 10, label: '10%' }
                        ]}
                    />
                    
                    <Typography gutterBottom sx={{ mt: 3 }}>
                        Минимальное количество кликов для анализа одного источника: {localThresholds.minClicks}
                    </Typography>
                    <Slider
                        value={localThresholds.minClicks}
                        onChange={(_, value) => handleChange('minClicks', value)}
                        min={100}
                        max={5000}
                        step={100}
                        valueLabelDisplay="auto"
                        marks={[
                            { value: 100, label: '100' },
                            { value: 1000, label: '1000' },
                            { value: 3000, label: '3000' },
                            { value: 5000, label: '5000' }
                        ]}
                    />
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Отмена</Button>
                <Button onClick={handleSave} variant="contained">Сохранить</Button>
            </DialogActions>
        </Dialog>
    );
};

const SitesStats = () => {
    const [loading, setLoading] = useState(false);
    const [sitesData, setSitesData] = useState([]);
    const [startDate, setStartDate] = useState(new Date());
    const [endDate, setEndDate] = useState(new Date());
    const [period, setPeriod] = useState('today');
    const [adsList, setAdsList] = useState([]);
    const [selectedAds, setSelectedAds] = useState([]);
    // Добавляем состояние для глобальных средних показателей
    const [globalAverages, setGlobalAverages] = useState({
        cr: 0,
        roi: 0
    });
    // Состояние для пороговых значений
    const [performanceThresholds, setPerformanceThresholds] = useState(DEFAULT_PERFORMANCE_THRESHOLDS);
    // Состояние для диалога настроек
    const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
    // Состояние для отображения сообщения об успешном обновлении
    const [updateMessage, setUpdateMessage] = useState('');
    // Добавляем состояния для пагинации
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(100);
    // Добавляем состояние для заблокированных площадок
    const [sitesList, setSitesList] = useState({
        blacklist: {},  // Черный список сайтов
        whitelist: {}   // Белый список сайтов
    });
    // Добавляем состояния для сортировки
    const [orderBy, setOrderBy] = useState('clicks');
    const [order, setOrder] = useState('desc');
    // Состояние для выбора текущего активного списка
    const [activeListType, setActiveListType] = useState('blacklist');
    
    useEffect(() => {
        // Установка дат в зависимости от выбранного периода
        const now = new Date();
        
        switch(period) {
            case 'today':
                // Начало дня: 00:00:00
                const todayStart = new Date();
                todayStart.setHours(0, 0, 0, 0);
                setStartDate(todayStart);
                
                // Для конца дня используем 23:59:59, чтобы охватить весь день
                // Это необходимо для корректного отображения данных за сегодня
                const todayEnd = new Date();
                todayEnd.setHours(23, 59, 59, 999);
                setEndDate(todayEnd);
                break;
            // Оставляем остальные периоды без изменений
            case 'yesterday':
                const yesterday = new Date();
                yesterday.setDate(yesterday.getDate() - 1);
                yesterday.setHours(0, 0, 0, 0);
                setStartDate(yesterday);
                const yesterdayEnd = new Date(yesterday);
                yesterdayEnd.setHours(23, 59, 59, 999);
                setEndDate(yesterdayEnd);
                break;
            case 'week':
                const weekStart = new Date();
                weekStart.setDate(weekStart.getDate() - 7);
                weekStart.setHours(0, 0, 0, 0);
                setStartDate(weekStart);
                
                // Для конца периода "неделя" используем конец текущего дня
                const weekEnd = new Date();
                weekEnd.setHours(23, 59, 59, 999);
                setEndDate(weekEnd);
                break;
            case 'month':
                const monthStart = new Date();
                monthStart.setMonth(monthStart.getMonth() - 1);
                monthStart.setHours(0, 0, 0, 0);
                setStartDate(monthStart);
                
                // Для конца периода "месяц" используем конец текущего дня
                const monthEnd = new Date();
                monthEnd.setHours(23, 59, 59, 999);
                setEndDate(monthEnd);
                break;
            default:
                // Не меняем даты, если выбран custom период
                break;
        }
    }, [period]);

    useEffect(() => {
        // Загрузка списка объявлений для фильтра
        loadAdsList();
    }, []);
    
    useEffect(() => {
        if (startDate && endDate) {
            loadStats();
        }
    }, [startDate, endDate, selectedAds]);

    // Загрузка списка объявлений
    const loadAdsList = async () => {
        try {
            // Запрашиваем все новости (без лимита)
            const response = await api.get('/admin/news', {
                params: {
                    limit: 1000, // Запрашиваем большее количество новостей
                    sort: 'popular' // Сортировка по популярности
                }
            });
            
            if (response.data && response.data.news && Array.isArray(response.data.news)) {
                console.log(`Загружено ${response.data.news.length} объявлений`);
                setAdsList(response.data.news.map(ad => ({
                    id: ad._id,
                    label: `${ad.orderNumber || ad._id} - ${ad.title || 'Без названия'}`
                })));
            }
        } catch (error) {
            console.error('Ошибка при загрузке списка объявлений:', error);
        }
    };

    // Форматирование даты в строку для ввода
    const formatDateForInput = (date) => {
        if (!date) return '';
        return date.toISOString().split('T')[0];
    };

    const loadStats = async (forceRefresh = false) => {
        if (!startDate || !endDate) return Promise.resolve(); // Возвращаем Promise.resolve если даты не заданы
        
        setLoading(true);
        try {
            // Клонируем даты, чтобы не менять исходные
            const startDateTime = new Date(startDate);
            startDateTime.setHours(0, 0, 0, 0);
            
            const endDateTime = new Date(endDate);
            // Всегда устанавливаем время конца дня, чтобы захватить все данные
            endDateTime.setHours(23, 59, 59, 999);
            
            console.log('Запрос статистики:', {
                период: period,
                startDate: startDateTime.toISOString(),
                endDate: endDateTime.toISOString(),
                localStartDate: startDateTime.toString(),
                localEndDate: endDateTime.toString(),
                объявления: selectedAds.length > 0 ? selectedAds.map(ad => ad.id).join(',') : 'все'
            });
            
            // Базовые параметры запроса с дополнительной информацией о часовом поясе
            const baseParams = {
                startDate: startDateTime.toISOString(),
                endDate: endDateTime.toISOString(),
                timezone: new Date().getTimezoneOffset() // Добавляем смещение часового пояса в минутах
            };
            
            // Настраиваем базовые опции запроса
            const baseRequestOptions = { 
                headers: forceRefresh ? {
                    'Cache-Control': 'no-cache, no-store, must-revalidate',
                    'Pragma': 'no-cache',
                    'Expires': '0'
                } : {}
            };
            
            let allLeadsResponse;
            let confirmedLeadsResponse;
            
            // Если выбрано несколько объявлений, делаем отдельные запросы и объединяем результаты
            if (selectedAds.length > 1) {
                console.log('Запрашиваем данные для нескольких объявлений:', selectedAds.length);
                
                // Массивы для хранения результатов запросов
                let allLeadsData = [];
                let confirmedLeadsData = [];
                
                // Делаем запросы для каждого объявления
                for (const ad of selectedAds) {
                    const adParams = { 
                        ...baseParams, 
                        newsId: ad.id 
                    };
                    
                    if (forceRefresh) {
                        adParams._t = new Date().getTime();
                        adParams.nocache = Math.random();
                    }
                    
                    const requestOptions = {
                        ...baseRequestOptions,
                        params: adParams
                    };
                    
                    // Запрос для всех лидов по этому объявлению
                    const adLeadsResponse = await api.get('/admin/stats/sites', requestOptions);
                    console.log(`Получено данных для объявления ${ad.id}:`, adLeadsResponse.data.length);
                    
                    // Добавляем данные в общий массив
                    allLeadsData = allLeadsData.concat(adLeadsResponse.data);
                    
                    // Запрос для подтвержденных лидов
                    const confirmedParams = {
                        ...adParams,
                        leadsStatus: "confirmed"
                    };
                    
                    const confirmedRequestOptions = {
                        ...baseRequestOptions,
                        params: confirmedParams
                    };
                    
                    const adConfirmedResponse = await api.get('/admin/stats/sites', confirmedRequestOptions);
                    confirmedLeadsData = confirmedLeadsData.concat(adConfirmedResponse.data);
                }
                
                // Агрегируем данные по siteId, так как один и тот же сайт может встречаться в разных объявлениях
                const aggregatedAllLeadsData = [];
                const sitesMap = new Map();
                
                // Объединяем данные по всем лидам
                allLeadsData.forEach(siteData => {
                    if (sitesMap.has(siteData.siteId)) {
                        // Если сайт уже есть в нашем Map, суммируем показатели
                        const existingSite = sitesMap.get(siteData.siteId);
                        existingSite.clicks += siteData.clicks;
                        existingSite.leads += siteData.leads;
                        existingSite.cost += siteData.cost;
                        // Другие показатели, если есть необходимость
                    } else {
                        // Если сайта ещё нет, добавляем его в Map
                        sitesMap.set(siteData.siteId, { ...siteData });
                    }
                });
                
                // Преобразуем Map обратно в массив
                sitesMap.forEach(site => {
                    aggregatedAllLeadsData.push(site);
                });
                
                // То же самое для подтвержденных лидов
                const confirmedSitesMap = new Map();
                const aggregatedConfirmedLeadsData = [];
                
                confirmedLeadsData.forEach(siteData => {
                    if (confirmedSitesMap.has(siteData.siteId)) {
                        const existingSite = confirmedSitesMap.get(siteData.siteId);
                        existingSite.clicks += siteData.clicks;
                        existingSite.leads += siteData.leads;
                        existingSite.cost += siteData.cost;
                        existingSite.revenue += siteData.revenue;
                    } else {
                        confirmedSitesMap.set(siteData.siteId, { ...siteData });
                    }
                });
                
                confirmedSitesMap.forEach(site => {
                    aggregatedConfirmedLeadsData.push(site);
                });
                
                // Создаем объекты, имитирующие ответы API с агрегированными данными
                allLeadsResponse = { data: aggregatedAllLeadsData };
                confirmedLeadsResponse = { data: aggregatedConfirmedLeadsData };
                
                console.log('Количество уникальных сайтов после агрегации:', aggregatedAllLeadsData.length);
            } else {
                // Если выбрано одно объявление или не выбрано ни одного, делаем обычный запрос
                const params = { ...baseParams };
                
                if (selectedAds.length === 1) {
                    params.newsId = selectedAds[0].id;
                    console.log('Отправляем запрос с фильтром по объявлению:', selectedAds[0].id);
                }
                
                if (forceRefresh) {
                    params._t = new Date().getTime();
                    params.nocache = Math.random();
                }
                
                const requestOptions = {
                    ...baseRequestOptions,
                    params
                };
                
                // Запрос со всеми лидами для подсчета общего количества
                allLeadsResponse = await api.get('/admin/stats/sites', requestOptions);
                console.log('Получены данные по сайтам:', allLeadsResponse.data.length, 'записей');
                
                // Дополняем параметры для запроса подтвержденных лидов
                const confirmedParams = {
                    ...params,
                    leadsStatus: "confirmed"
                };
                
                // Настраиваем опции запроса для подтвержденных лидов
                const confirmedRequestOptions = {
                    ...baseRequestOptions,
                    params: confirmedParams
                };
                
                // Запрос только с подтвержденными лидами для подсчета дохода
                confirmedLeadsResponse = await api.get('/admin/stats/sites', confirmedRequestOptions);
            }
            
            // Объединяем данные: берем количество лидов из первого запроса, а доход из второго
            const combinedData = allLeadsResponse.data.map(siteData => {
                // Ищем соответствующую запись с подтвержденными лидами
                const confirmedData = confirmedLeadsResponse.data.find(item => item.siteId === siteData.siteId);
                
                return {
                    ...siteData,
                    // Оставляем общее количество лидов
                    revenue: confirmedData ? confirmedData.revenue : 0, // Берем доход только от подтвержденных лидов
                    confirmedLeads: confirmedData ? confirmedData.leads : 0 // Добавляем количество подтвержденных лидов
                };
            });
            
            // Рассчитываем глобальные средние показатели
            const totalClicks = combinedData.reduce((sum, site) => sum + site.clicks, 0);
            const totalLeads = combinedData.reduce((sum, site) => sum + site.leads, 0);
            const totalRevenue = combinedData.reduce((sum, site) => sum + site.revenue, 0);
            const totalCost = combinedData.reduce((sum, site) => sum + site.cost, 0);
            
            setGlobalAverages({
                cr: totalClicks > 0 ? (totalLeads / totalClicks * 100) : 0,
                roi: totalCost > 0 ? (((totalRevenue - totalCost) / totalCost) * 100) : 0
            });
            
            // Проверяем и логируем итоговые данные для отладки
            console.log('Итоговые данные:', {
                записей: combinedData.length,
                клики: totalClicks,
                лиды: totalLeads,
                доход: totalRevenue,
                расход: totalCost
            });
            
            // После получения и комбинирования данных, сортируем их
            const sortedData = combinedData.sort(getSorting(order, orderBy));
            
            setSitesData(sortedData);
            return Promise.resolve(sortedData); // Возвращаем Promise с данными
        } catch (error) {
            console.error('Ошибка при загрузке статистики по сайтам:', error);
            return Promise.reject(error); // Возвращаем Promise с ошибкой
        } finally {
            setLoading(false);
        }
    };

    const handleRefresh = () => {
        console.log('Нажата кнопка "Обновить"', {
            startDate: startDate.toISOString(),
            endDate: endDate.toISOString(),
            selectedAds: selectedAds.map(ad => ad.id).join(',')
        });
        
        // Очищаем предыдущее сообщение об обновлении
        setUpdateMessage('');
        
        // Показываем индикатор загрузки
        setLoading(true);
        
        // Принудительно обновляем данные
        loadStats(true)
            .then(data => {
                // Показываем сообщение об успешном обновлении
                const currentTime = new Date().toLocaleTimeString();
                setUpdateMessage(`Данные успешно обновлены в ${currentTime}`);
                
                // Скрываем сообщение через 5 секунд
                setTimeout(() => {
                    setUpdateMessage('');
                }, 5000);
            })
            .catch(error => {
                console.error('Ошибка при обновлении данных:', error);
                setUpdateMessage('Ошибка при обновлении данных. Проверьте соединение с сервером.');
            })
            .finally(() => {
                console.log('Обновление данных завершено');
            });
    };

    const handlePeriodChange = (event) => {
        setPeriod(event.target.value);
    };

    const handleStartDateChange = (event) => {
        setStartDate(new Date(event.target.value));
    };

    const handleEndDateChange = (event) => {
        setEndDate(new Date(event.target.value));
    };
    
    const handleAdChange = (event, newValue) => {
        setSelectedAds(newValue);
    };

    // Сохранение пороговых значений
    const handleSaveThresholds = (newThresholds) => {
        setPerformanceThresholds(newThresholds);
        
        // Можно также сохранить в localStorage для сохранения между сессиями
        localStorage.setItem('siteStatsThresholds', JSON.stringify(newThresholds));
    };
    
    // Загрузка сохраненных пороговых значений при инициализации
    useEffect(() => {
        const savedThresholds = localStorage.getItem('siteStatsThresholds');
        if (savedThresholds) {
            try {
                const parsedThresholds = JSON.parse(savedThresholds);
                setPerformanceThresholds(parsedThresholds);
            } catch (e) {
                console.error('Ошибка при загрузке сохраненных настроек:', e);
            }
        }
    }, []);
    
    // Передаем актуальные пороги в функцию оценки эффективности
    const evaluateSitePerformance = (siteData, globalAvgs) => {
        // Проверяем, применен ли фильтр по объявлению
        const isFiltered = selectedAds.length > 0;
        return evaluatePlatformPerformance(siteData, globalAvgs, performanceThresholds, isFiltered);
    };

    // Отображение эффективности в таблице
    const renderPerformanceStatus = (performance) => {
        switch (performance.status) {
            case 'critical':
                return (
                    <Tooltip title={performance.warnings.map(w => w.message).join('\n')}>
                        <ErrorIcon color="error" />
                    </Tooltip>
                );
            case 'warning':
                return (
                    <Tooltip title={performance.warnings.map(w => w.message).join('\n')}>
                        <WarningIcon color="warning" />
                    </Tooltip>
                );
            case 'info':
                return (
                    <Tooltip title="Недостаточно данных для анализа">
                        <Chip 
                            label="Мало данных" 
                            color="info" 
                            size="small" 
                            variant="outlined" 
                        />
                    </Tooltip>
                );
            default:
                return (
                    <Tooltip title="Хорошая эффективность">
                        <CheckCircleIcon color="success" />
                    </Tooltip>
                );
        }
    };

    // Обработчики пагинации
    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };
    
    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };
    
    // Получаем только данные для текущей страницы
    const paginatedSitesData = sitesData.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
    );

    // Загружаем заблокированные площадки из localStorage при инициализации
    useEffect(() => {
        const savedBlockedSites = localStorage.getItem('blockedSites');
        if (savedBlockedSites) {
            try {
                const parsedBlockedSites = JSON.parse(savedBlockedSites);
                // Проверяем структуру: если это объект с blacklist и whitelist, используем как есть
                if (parsedBlockedSites.blacklist && parsedBlockedSites.whitelist) {
                    setSitesList(parsedBlockedSites);
                } else {
                    // Если это старый формат (только черный список), конвертируем
                    setSitesList({
                        blacklist: parsedBlockedSites,
                        whitelist: {}
                    });
                    // Обновляем localStorage с новой структурой
                    localStorage.setItem('blockedSites', JSON.stringify({
                        blacklist: parsedBlockedSites,
                        whitelist: {}
                    }));
                }
            } catch (e) {
                console.error('Ошибка при загрузке заблокированных площадок:', e);
            }
        }
    }, []);
    
    // Обработчик изменения состояния чекбокса блокировки
    const handleSiteListToggle = (siteId) => {
        const newSitesList = { ...sitesList };
        
        // Если сайт уже есть в текущем активном списке, удаляем его оттуда
        if (newSitesList[activeListType][siteId]) {
            delete newSitesList[activeListType][siteId];
        } else {
            // Иначе добавляем в текущий список
            newSitesList[activeListType][siteId] = true;
            
            // Если сайт был в противоположном списке, удаляем его оттуда
            const oppositeList = activeListType === 'blacklist' ? 'whitelist' : 'blacklist';
            if (newSitesList[oppositeList][siteId]) {
                delete newSitesList[oppositeList][siteId];
            }
        }
        
        setSitesList(newSitesList);
        
        // Сохраняем в localStorage
        localStorage.setItem('blockedSites', JSON.stringify(newSitesList));
    };
    
    // Обработчик переключения типа списка
    const handleListTypeChange = (event) => {
        setActiveListType(event.target.value);
    };

    // Функция для копирования ID сайтов в буфер обмена
    const copySiteIdsToClipboard = () => {
        // Получаем текущий активный список
        const activeList = sitesList[activeListType];
        
        // Получаем ключи объекта (ID сайтов)
        const siteIds = Object.keys(activeList);
        
        if (siteIds.length === 0) {
            alert(`${activeListType === 'blacklist' ? 'Черный' : 'Белый'} список пуст`);
            return;
        }
        
        // Формируем строку с ID через запятую
        const idString = siteIds.join(',');
        
        // Копируем в буфер обмена
        navigator.clipboard.writeText(idString)
            .then(() => {
                alert(`ID сайтов скопированы в буфер обмена: ${idString}`);
            })
            .catch(err => {
                console.error('Ошибка при копировании в буфер обмена:', err);
                alert('Не удалось скопировать ID сайтов. Проверьте консоль для деталей.');
            });
    };

    // Обработчик изменения сортировки
    const handleRequestSort = (property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    // Функция сравнения для сортировки
    const getSorting = (order, orderBy) => {
        return order === 'desc'
            ? (a, b) => {
                // Рассчитываем значения в зависимости от колонки
                let aValue, bValue;
                
                switch(orderBy) {
                    case 'cr':
                        aValue = a.clicks > 0 ? (a.leads / a.clicks * 100) : 0;
                        bValue = b.clicks > 0 ? (b.leads / b.clicks * 100) : 0;
                        break;
                    case 'approveRate':
                        aValue = a.leads > 0 ? (a.confirmedLeads / a.leads * 100) : 0;
                        bValue = b.leads > 0 ? (b.confirmedLeads / b.leads * 100) : 0;
                        break;
                    case 'profit':
                        aValue = a.revenue - a.cost;
                        bValue = b.revenue - b.cost;
                        break;
                    case 'roi':
                        const aCost = a.cost || 0;
                        const bCost = b.cost || 0;
                        aValue = aCost > 0 ? ((a.revenue - aCost) / aCost * 100) : 0;
                        bValue = bCost > 0 ? ((b.revenue - bCost) / bCost * 100) : 0;
                        break;
                    default:
                        aValue = a[orderBy] || 0;
                        bValue = b[orderBy] || 0;
                }
                
                if (bValue < aValue) return -1;
                if (bValue > aValue) return 1;
                return 0;
            }
            : (a, b) => {
                // То же самое, но для восходящего порядка
                let aValue, bValue;
                
                switch(orderBy) {
                    case 'cr':
                        aValue = a.clicks > 0 ? (a.leads / a.clicks * 100) : 0;
                        bValue = b.clicks > 0 ? (b.leads / b.clicks * 100) : 0;
                        break;
                    case 'approveRate':
                        aValue = a.leads > 0 ? (a.confirmedLeads / a.leads * 100) : 0;
                        bValue = b.leads > 0 ? (b.confirmedLeads / b.leads * 100) : 0;
                        break;
                    case 'profit':
                        aValue = a.revenue - a.cost;
                        bValue = b.revenue - b.cost;
                        break;
                    case 'roi':
                        const aCost = a.cost || 0;
                        const bCost = b.cost || 0;
                        aValue = aCost > 0 ? ((a.revenue - aCost) / aCost * 100) : 0;
                        bValue = bCost > 0 ? ((b.revenue - bCost) / bCost * 100) : 0;
                        break;
                    default:
                        aValue = a[orderBy] || 0;
                        bValue = b[orderBy] || 0;
                }
                
                if (aValue < bValue) return -1;
                if (aValue > bValue) return 1;
                return 0;
            };
    };

    useEffect(() => {
        // Сортируем данные при изменении параметров сортировки или данных
        if (sitesData.length > 0) {
            const sortedData = [...sitesData].sort(getSorting(order, orderBy));
            setSitesData(sortedData);
        }
    }, [order, orderBy]);

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h5" component="h2">
                    Статистика по рекламным площадкам
                </Typography>
                <Button 
                    variant="contained" 
                    color="primary" 
                    onClick={() => setSettingsDialogOpen(true)}
                    startIcon={<SettingsIcon />}
                >
                    Настройки параметров эффективности
                </Button>
            </Box>
            
            {/* Сообщение об обновлении */}
            {updateMessage && (
                <Paper 
                    sx={{ 
                        p: 1, 
                        mb: 2, 
                        bgcolor: updateMessage.includes('Ошибка') ? 'error.light' : 'success.light',
                        color: 'white',
                        display: 'flex',
                        justifyContent: 'center'
                    }}
                >
                    <Typography>{updateMessage}</Typography>
                </Paper>
            )}
            
            {/* Переключатель типа списка */}
            <Paper sx={{ p: 2, mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <FormControl component="fieldset">
                    <FormLabel component="legend">Тип списка для управления сайтами</FormLabel>
                    <RadioGroup
                        row
                        name="list-type"
                        value={activeListType}
                        onChange={handleListTypeChange}
                    >
                        <FormControlLabel 
                            value="blacklist" 
                            control={<Radio />} 
                            label={
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Typography sx={{ color: 'error.main' }}>Черный список</Typography>
                                    <Chip 
                                        size="small" 
                                        label={Object.keys(sitesList.blacklist).length} 
                                        sx={{ ml: 1, bgcolor: 'error.main', color: 'white' }}
                                    />
                                </Box>
                            }
                        />
                        <FormControlLabel 
                            value="whitelist" 
                            control={<Radio />} 
                            label={
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Typography sx={{ color: 'success.main' }}>Белый список</Typography>
                                    <Chip 
                                        size="small" 
                                        label={Object.keys(sitesList.whitelist).length} 
                                        sx={{ ml: 1, bgcolor: 'success.main', color: 'white' }}
                                    />
                                </Box>
                            }
                        />
                    </RadioGroup>
                </FormControl>
                <Typography variant="body2" color="textSecondary">
                    {activeListType === 'blacklist' 
                        ? 'Отмечайте сайты, которые нужно заблокировать' 
                        : 'Отмечайте сайты, которые должны быть в приоритете'}
                </Typography>
                <Box>
                    <Button 
                        variant="outlined" 
                        color={activeListType === 'blacklist' ? 'error' : 'success'}
                        size="small"
                        onClick={() => {
                            if (window.confirm(`Вы уверены, что хотите очистить ${activeListType === 'blacklist' ? 'черный' : 'белый'} список?`)) {
                                const newSitesList = { ...sitesList };
                                newSitesList[activeListType] = {};
                                setSitesList(newSitesList);
                                localStorage.setItem('blockedSites', JSON.stringify(newSitesList));
                            }
                        }}
                        sx={{ mr: 1 }}
                    >
                        Очистить {activeListType === 'blacklist' ? 'черный' : 'белый'} список
                    </Button>
                    <Tooltip title={`Нажмите здесь, чтобы скопировать все ID сайтов из ${activeListType === 'blacklist' ? 'черного' : 'белого'} списка через запятую`}>
                        <Button
                            variant="contained"
                            color={activeListType === 'blacklist' ? 'error' : 'success'}
                            size="small"
                            onClick={copySiteIdsToClipboard}
                            sx={{ 
                                fontWeight: 'bold',
                                boxShadow: '0 0 5px rgba(0,0,0,0.3)',
                                '&:hover': {
                                    boxShadow: '0 0 10px rgba(0,0,0,0.5)'
                                }
                            }}
                            startIcon={<ContentCopyIcon />}
                        >
                            Копировать ID сайтов
                        </Button>
                    </Tooltip>
                </Box>
            </Paper>
            
            {/* Диалог настроек */}
            <ThresholdsSettingsDialog 
                open={settingsDialogOpen}
                onClose={() => setSettingsDialogOpen(false)}
                thresholds={performanceThresholds}
                onSave={handleSaveThresholds}
            />
            
            {/* Отображаем список проблемных площадок */}
            {!loading && sitesData.length > 0 && (
                <ProblemSitesList 
                    sites={sitesData} 
                    globalAverages={globalAverages}
                    thresholds={performanceThresholds}
                    isFiltered={selectedAds.length > 0}
                />
            )}
            
            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} md={4}>
                    <FormControl fullWidth>
                        <InputLabel id="period-select-label">Период</InputLabel>
                        <Select
                            labelId="period-select-label"
                            id="period-select"
                            value={period}
                            label="Период"
                            onChange={handlePeriodChange}
                        >
                            <MenuItem value="today">Сегодня</MenuItem>
                            <MenuItem value="yesterday">Вчера</MenuItem>
                            <MenuItem value="week">Последние 7 дней</MenuItem>
                            <MenuItem value="month">Последние 30 дней</MenuItem>
                            <MenuItem value="custom">Произвольный период</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>
                
                {period === 'custom' && (
                    <>
                        <Grid item xs={12} md={4}>
                            <TextField
                                label="С даты"
                                type="date"
                                value={formatDateForInput(startDate)}
                                onChange={handleStartDateChange}
                                fullWidth
                                InputLabelProps={{
                                    shrink: true,
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <TextField
                                label="По дату"
                                type="date"
                                value={formatDateForInput(endDate)}
                                onChange={handleEndDateChange}
                                fullWidth
                                InputLabelProps={{
                                    shrink: true,
                                }}
                                inputProps={{
                                    min: formatDateForInput(startDate)
                                }}
                            />
                        </Grid>
                    </>
                )}
                
                <Grid item xs={12} md={period === 'custom' ? 12 : 8}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Autocomplete
                            id="ad-select"
                            multiple
                            options={adsList}
                            value={selectedAds}
                            onChange={handleAdChange}
                            getOptionLabel={(option) => option.label}
                            filterOptions={(options, state) => {
                                // Если строка поиска пустая, возвращаем первые 10 наиболее популярных объявлений
                                if (!state.inputValue) {
                                    return options.slice(0, 10);
                                }
                                
                                // Иначе фильтруем по всем объявлениям
                                const filtered = options.filter(option => 
                                    option.label.toLowerCase().includes(state.inputValue.toLowerCase())
                                );
                                
                                return filtered;
                            }}
                            renderInput={(params) => (
                                <TextField 
                                    {...params} 
                                    label="Фильтр по объявлениям" 
                                    placeholder={selectedAds.length === 0 ? "Все объявления" : ""}
                                />
                            )}
                            renderTags={(selected, getTagProps) =>
                                selected.map((option, index) => (
                                    <Chip
                                        {...getTagProps({ index })}
                                        key={option.id}
                                        label={option.label}
                                        size="small"
                                    />
                                ))
                            }
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            sx={{ flexGrow: 1 }}
                        />
                        {selectedAds.length > 0 && (
                            <Button 
                                variant="outlined"
                                onClick={() => setSelectedAds([])}
                                sx={{ minWidth: 'auto', height: 56 }}
                            >
                                Очистить
                            </Button>
                        )}
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleRefresh}
                            sx={{ minWidth: 'auto', height: 56 }}
                            startIcon={<RefreshIcon />}
                        >
                            Обновить
                        </Button>
                    </Box>
                </Grid>
            </Grid>
            
            {/* Отладочная информация о выбранных параметрах */}
            <Box sx={{ my: 2 }}>
                <Typography variant="body2" color="textSecondary">
                    Период: {period}, 
                    C: {startDate?.toLocaleDateString('ru-RU')} {startDate?.toLocaleTimeString('ru-RU')},
                    По: {endDate?.toLocaleDateString('ru-RU')} {endDate?.toLocaleTimeString('ru-RU')},
                    Объявления: {selectedAds.length > 0 ? selectedAds.map(ad => ad.id).join(',') : 'все'}
                </Typography>
                {selectedAds.length > 1 && (
                    <Typography variant="body2" color="primary">
                        Выбрано {selectedAds.length} объявлений. Статистика агрегирована по сайтам.
                    </Typography>
                )}
            </Box>
            
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <>
                    <TableContainer component={Paper}>
                        <Table sx={{ minWidth: 650 }} aria-label="статистика по сайтам">
                            <TableHead>
                                <TableRow>
                                    <TableCell>
                                        {activeListType === 'blacklist' 
                                            ? <Typography color="error.main">Черный список</Typography> 
                                            : <Typography color="success.main">Белый список</Typography>}
                                    </TableCell>
                                    <TableCell>ID сайта</TableCell>
                                    <TableCell>Статус эффективности</TableCell>
                                    <TableCell>
                                        <TableSortLabel
                                            active={orderBy === 'clicks'}
                                            direction={orderBy === 'clicks' ? order : 'asc'}
                                            onClick={() => handleRequestSort('clicks')}
                                        >
                                            Клики
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell>
                                        <TableSortLabel
                                            active={orderBy === 'leads'}
                                            direction={orderBy === 'leads' ? order : 'asc'}
                                            onClick={() => handleRequestSort('leads')}
                                        >
                                            Лиды
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell>
                                        <TableSortLabel
                                            active={orderBy === 'cr'}
                                            direction={orderBy === 'cr' ? order : 'asc'}
                                            onClick={() => handleRequestSort('cr')}
                                        >
                                            CR (%)
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell>
                                        <TableSortLabel
                                            active={orderBy === 'approveRate'}
                                            direction={orderBy === 'approveRate' ? order : 'asc'}
                                            onClick={() => handleRequestSort('approveRate')}
                                        >
                                            Аппрув-рейт (%)
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell>
                                        <TableSortLabel
                                            active={orderBy === 'cost'}
                                            direction={orderBy === 'cost' ? order : 'asc'}
                                            onClick={() => handleRequestSort('cost')}
                                        >
                                            Расход (руб.)
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell>
                                        <TableSortLabel
                                            active={orderBy === 'revenue'}
                                            direction={orderBy === 'revenue' ? order : 'asc'}
                                            onClick={() => handleRequestSort('revenue')}
                                        >
                                            Доход (руб.)
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell>
                                        <TableSortLabel
                                            active={orderBy === 'profit'}
                                            direction={orderBy === 'profit' ? order : 'asc'}
                                            onClick={() => handleRequestSort('profit')}
                                        >
                                            Прибыль (руб.)
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell>
                                        <TableSortLabel
                                            active={orderBy === 'roi'}
                                            direction={orderBy === 'roi' ? order : 'asc'}
                                            onClick={() => handleRequestSort('roi')}
                                        >
                                            ROI (%)
                                        </TableSortLabel>
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {sitesData.length > 0 ? (
                                    paginatedSitesData.map((site) => {
                                        const cr = site.clicks > 0 ? (site.leads / site.clicks * 100) : 0;
                                        const profit = site.revenue - site.cost;
                                        const roi = site.cost > 0 ? ((profit / site.cost) * 100) : 0;
                                        const approveRate = site.leads > 0 ? (site.confirmedLeads / site.leads * 100) : 0;
                                        
                                        // Оцениваем эффективность площадки
                                        const performance = evaluateSitePerformance(
                                            {
                                                ...site,
                                                cr,
                                                roi,
                                                profit,
                                                approveRate
                                            },
                                            globalAverages
                                        );

                                        // Проверяем, в каком списке находится сайт
                                        const isBlacklisted = Boolean(sitesList.blacklist[site.siteId]);
                                        const isWhitelisted = Boolean(sitesList.whitelist[site.siteId]);
                                        
                                        // Определяем, отмечен ли сайт в текущем активном списке
                                        const isChecked = activeListType === 'blacklist' 
                                            ? isBlacklisted 
                                            : isWhitelisted;
                                        
                                        return (
                                            <TableRow 
                                                key={site.siteId} 
                                                sx={{ 
                                                    '&:last-child td, &:last-child th': { border: 0 },
                                                    // Подсветка строк в зависимости от типа списка
                                                    bgcolor: isBlacklisted 
                                                        ? 'rgba(255, 0, 0, 0.15)' // Красный для черного списка
                                                        : isWhitelisted
                                                            ? 'rgba(0, 255, 0, 0.15)' // Зеленый для белого списка
                                                            : performance.hasEnoughData 
                                                                ? (performance.status === 'critical' 
                                                                    ? 'rgba(255, 0, 0, 0.05)' 
                                                                    : performance.status === 'warning' 
                                                                        ? 'rgba(255, 152, 0, 0.05)' 
                                                                        : 'inherit')
                                                                : 'inherit'
                                                }}
                                            >
                                                <TableCell padding="checkbox">
                                                    <Checkbox
                                                        checked={isChecked}
                                                        onChange={() => handleSiteListToggle(site.siteId)}
                                                        color={activeListType === 'blacklist' ? 'error' : 'success'}
                                                        title={activeListType === 'blacklist' 
                                                            ? 'Добавить в черный список' 
                                                            : 'Добавить в белый список'}
                                                    />
                                                </TableCell>
                                                <TableCell component="th" scope="row">
                                                    {site.siteId === 'unknown' ? (
                                                        <Tooltip title="Клики без указания конкретного сайта">
                                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                                <span>Неизвестный сайт</span>
                                                                <InfoIcon fontSize="small" sx={{ ml: 1, color: 'text.secondary' }} />
                                                            </Box>
                                                        </Tooltip>
                                                    ) : (
                                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                            {site.siteId}
                                                            {isBlacklisted && (
                                                                <Chip 
                                                                    size="small" 
                                                                    label="ЧС" 
                                                                    sx={{ ml: 1, bgcolor: 'error.main', color: 'white' }}
                                                                />
                                                            )}
                                                            {isWhitelisted && (
                                                                <Chip 
                                                                    size="small" 
                                                                    label="БС" 
                                                                    sx={{ ml: 1, bgcolor: 'success.main', color: 'white' }}
                                                                />
                                                            )}
                                                        </Box>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    {renderPerformanceStatus(performance)}
                                                </TableCell>
                                                <TableCell>{site.clicks}</TableCell>
                                                <TableCell>{site.leads}</TableCell>
                                                <TableCell sx={{ color: getCRColor(cr) }}>
                                                    {cr.toFixed(2)}
                                                </TableCell>
                                                <TableCell sx={{ color: getApproveRateColor(approveRate) }}>
                                                    {approveRate.toFixed(2)}
                                                </TableCell>
                                                <TableCell>{site.cost.toFixed(2)}</TableCell>
                                                <TableCell>{site.revenue.toFixed(2)}</TableCell>
                                                <TableCell sx={{ color: getProfitColor(profit) }}>
                                                    {profit.toFixed(2)}
                                                </TableCell>
                                                <TableCell sx={{ 
                                                    color: getRoiColor(roi),
                                                    fontWeight: Math.abs(roi) > 50 ? 'bold' : 'normal'
                                                }}>
                                                    {roi.toFixed(2)}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={11} align="center">
                                            Нет данных за выбранный период
                                        </TableCell>
                                    </TableRow>
                                )}
                                {sitesData.length > 0 && (
                                    <TableRow
                                        sx={{ 
                                            '& td': { 
                                                fontWeight: 'bold', 
                                                borderTop: '2px solid rgba(224, 224, 224, 1)'
                                            } 
                                        }}
                                    >
                                        <TableCell></TableCell>
                                        <TableCell component="th" scope="row">ИТОГО</TableCell>
                                        <TableCell></TableCell>
                                        <TableCell>
                                            {sitesData.reduce((sum, site) => sum + site.clicks, 0)}
                                        </TableCell>
                                        <TableCell>
                                            {sitesData.reduce((sum, site) => sum + site.leads, 0)}
                                        </TableCell>
                                        <TableCell>
                                            {(() => {
                                                const totalClicks = sitesData.reduce((sum, site) => sum + site.clicks, 0);
                                                const totalLeads = sitesData.reduce((sum, site) => sum + site.leads, 0);
                                                const totalCR = totalClicks > 0 ? (totalLeads / totalClicks * 100) : 0;
                                                return (
                                                    <span style={{ color: getCRColor(totalCR) }}>
                                                        {totalCR.toFixed(2)}
                                                    </span>
                                                );
                                            })()}
                                        </TableCell>
                                        <TableCell>
                                            {(() => {
                                                const totalLeads = sitesData.reduce((sum, site) => sum + site.leads, 0);
                                                const totalConfirmedLeads = sitesData.reduce((sum, site) => sum + site.confirmedLeads, 0);
                                                const totalApproveRate = totalLeads > 0 ? (totalConfirmedLeads / totalLeads * 100) : 0;
                                                return (
                                                    <span style={{ color: getApproveRateColor(totalApproveRate) }}>
                                                        {totalApproveRate.toFixed(2)}
                                                    </span>
                                                );
                                            })()}
                                        </TableCell>
                                        <TableCell>
                                            {sitesData.reduce((sum, site) => sum + site.cost, 0).toFixed(2)}
                                        </TableCell>
                                        <TableCell>
                                            {sitesData.reduce((sum, site) => sum + site.revenue, 0).toFixed(2)}
                                        </TableCell>
                                        <TableCell>
                                            {(() => {
                                                const totalRevenue = sitesData.reduce((sum, site) => sum + site.revenue, 0);
                                                const totalCost = sitesData.reduce((sum, site) => sum + site.cost, 0);
                                                const totalProfit = totalRevenue - totalCost;
                                                return (
                                                    <span style={{ color: getProfitColor(totalProfit) }}>
                                                        {totalProfit.toFixed(2)}
                                                    </span>
                                                );
                                            })()}
                                        </TableCell>
                                        <TableCell>
                                            {(() => {
                                                const totalRevenue = sitesData.reduce((sum, site) => sum + site.revenue, 0);
                                                const totalCost = sitesData.reduce((sum, site) => sum + site.cost, 0);
                                                const totalRoi = totalCost > 0 ? (((totalRevenue - totalCost) / totalCost) * 100) : 0;
                                                return (
                                                    <span style={{ 
                                                        color: getRoiColor(totalRoi),
                                                        fontWeight: Math.abs(totalRoi) > 50 ? 'bold' : 'normal'
                                                    }}>
                                                        {totalRoi.toFixed(2)}
                                                    </span>
                                                );
                                            })()}
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    
                    {/* Добавляем пагинацию */}
                    {sitesData.length > rowsPerPage && (
                        <TablePagination
                            rowsPerPageOptions={[25, 50, 100, 250, 500]}
                            component="div"
                            count={sitesData.length}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            labelRowsPerPage="Строк на странице:"
                            labelDisplayedRows={({from, to, count}) => `${from}-${to} из ${count}`}
                        />
                    )}
                </>
            )}
        </Box>
    );
};

export default SitesStats; 